import { useEffect, useRef, useState } from "react";
import { fetchHaltedLanes, type HaltedLane } from "../syncApi";

interface HaltedLanesAlertProps {
  /** Intervallo di polling in ms. Default 20s: lo stato "halted" è
   *  persistente (la lane non riparte fino al restart del backend), quindi
   *  non serve reattività al secondo — basta che l'avviso compaia presto. */
  pollMs?: number;
}

/**
 * Avviso sempre visibile quando il circuit breaker del supervisor ha
 * fermato una o più lane del sync engine ("lane zombie").
 *
 * Perché serve: ``SyncStatusPill`` resta verde anche con una lane spenta,
 * perché guarda solo i job (queued/running/24h), non il supervisor. Una
 * lane fermata dal breaker smette di lavorare in silenzio finché il backend
 * non viene riavviato: senza questo banner, Davide non se ne accorge.
 *
 * Comportamento:
 * - vuoto in condizioni normali → ritorna ``null`` (nessun rumore visivo);
 * - compare solo quando ``/api/sync/halted-lanes`` ritorna ≥ 1 lane;
 * - errori di rete / payload non validi sono silenziosi: la pill aggregata
 *   già segnala "Backend irraggiungibile", qui evitiamo falsi allarmi.
 */
export function HaltedLanesAlert({ pollMs = 20_000 }: HaltedLanesAlertProps) {
  const [lanes, setLanes] = useState<HaltedLane[]>([]);
  // Guard anti-race con il cleanup: evita setState dopo l'unmount (stesso
  // pattern di SyncDetailPanel).
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    let timerId: number | null = null;
    const controller = new AbortController();

    const tick = async () => {
      try {
        const data = await fetchHaltedLanes({ signal: controller.signal });
        if (cancelRef.current) return;
        // Validazione al boundary: non ci fidiamo della forma del payload
        // remoto. Qualsiasi cosa non sia un array → nessuna lane (no crash).
        setLanes(Array.isArray(data) ? data : []);
      } catch {
        // Silenzioso di proposito: niente falsi allarmi se il backend è
        // irraggiungibile o l'endpoint fallisce.
      }
      if (cancelRef.current) return;
      timerId = window.setTimeout(() => void tick(), pollMs);
    };
    void tick();

    return () => {
      cancelRef.current = true;
      controller.abort();
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [pollMs]);

  if (lanes.length === 0) return null;

  const headline =
    lanes.length === 1
      ? "Sincronizzazione interrotta: una lane è stata fermata dal sistema"
      : `Sincronizzazione interrotta: ${lanes.length} lane sono state fermate dal sistema`;

  return (
    <div
      role="alert"
      className="mt-2 w-full rounded-md border border-red/30 bg-red-soft px-3 py-2 text-[11.5px] text-ink leading-relaxed"
    >
      <div className="flex items-center gap-2 font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-red shrink-0" />
        {headline}
      </div>
      <ul aria-label="Lane fermate" className="mt-1.5 space-y-1">
        {lanes.map((l) => (
          // key composta lane+timestamp: il backend garantisce una lane per
          // chiave ``sync_lane.<name>.halted``, ma cosi' siamo robusti anche a
          // eventuali duplicati nel payload (niente warning React).
          <li key={`${l.lane}-${l.halted_at}`} className="flex flex-wrap items-baseline gap-x-2">
            <code className="font-mono font-medium text-red">{l.lane}</code>
            <span className="text-ink-2">{l.reason}</span>
            <time dateTime={l.halted_at} className="text-ink-3 font-mono tabular-nums">
              {formatHaltedAt(l.halted_at)}
            </time>
          </li>
        ))}
      </ul>
      <div className="mt-1.5 text-ink-2">
        Le lane fermate non ripartono da sole: <strong>riavvia il backend</strong> (chiudi e riapri
        Order Manager) per ripristinarle.
      </div>
    </div>
  );
}

/**
 * Formatta un timestamp ISO (UTC) come data+ora locale italiana
 * ("24/06/2026, 14:00"). Se non è parsabile ritorna la stringa grezza,
 * così non si perde mai l'informazione.
 */
function formatHaltedAt(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  return new Date(ts).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
